/* eslint-disable max-len */
import Moment from 'moment-timezone';
import React, { useState, useEffect } from 'react';
import { Box, Chip, Typography, Button } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Pagination } from '@material-ui/lab';

import useStyle from './style';

import { getSelectedBusiness } from 'store/features/business/business.selector';
import BasicTable from 'components/table';
import ErrorIcon from 'components/error/error-icon';
import { openNewTab } from 'helpers/open-new-tab';
import { AspType } from 'types';
import { getByodUploadFiles } from 'store/features/report/report.selector';
import { fetchByodUploadFilesApi } from 'store/features/report/report.action';
import { ScrollView } from 'components';

const DataUploads = (): JSX.Element => {
  const PAGINATION_LIMIT = 10;
  const { t } = useTranslation();
  const classes = useStyle();
  const dispatch = useDispatch();
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [queryParams, setQueryParams] = useState(null);

  const selectedBusiness = useSelector(getSelectedBusiness);
  const [connection, setConnection] = useState(null);
  const { data: byodData, pagination, error } = useSelector(getByodUploadFiles);

  const isLoading = useSelector(getByodUploadFiles).loading;

  const format = 'D MMM YYYY h:mm:ss A';

  const formatRows = (unFormattedData: any[]): any[] => {
    if (!unFormattedData) return [];

    const formattedRows = [];
    unFormattedData?.map((dataObj) => {
      const fileRows = dataObj?.files?.map((fileObj) => {
        return {
          name: fileObj?.name || '-',
          syncStatus: fileObj?.status || '-',
          timeStamp: Moment(dataObj?.requestedOn).format(format) || '-',
          path: fileObj?.path || '-',
          pushCommId: dataObj?.pushCommunicationId || '-',
        };
      });
      formattedRows.push(...fileRows);
    });

    return formattedRows;
  };

  const refreshByodFileList = (params): void => {
    let localOffset = Number((currentPage - 1) * PAGINATION_LIMIT);
    if (params?.offset !== undefined) localOffset = params?.offset;
    const payload = {
      connectionUuid: connection.connectionUuid,
      limit: params?.limit || PAGINATION_LIMIT,
      offset: localOffset,
    };
    dispatch(fetchByodUploadFilesApi({ params: payload }));
  };

  useEffect(() => {
    const rowsFormatted = formatRows(byodData);
    setRows(rowsFormatted);
    setTotalPages(Math.ceil(pagination?.count / PAGINATION_LIMIT));
  }, [byodData]);

  useEffect(() => {
    if (connection?.connectionUuid) {
      dispatch(fetchByodUploadFilesApi({ params: queryParams }));
    }
  }, [connection]);

  const getByodConnection = (): void => {
    const byodConnection = selectedBusiness?.connections?.find(
      (connection) => connection.serviceName === AspType.BYOD,
    );
    setQueryParams({
      connectionUuid: byodConnection?.connectionId,
      limit: PAGINATION_LIMIT,
      offset: 0,
    });
    setConnection(byodConnection);
  };

  useEffect(() => {
    if (selectedBusiness?.connections?.length) {
      getByodConnection();
    }
  }, [selectedBusiness]);

  const columns = [
    {
      key: 'name',
      name: t('DASHBOARD_ENUM_REPORT_TABLE_HEADER_NAME'),
      className: classes.firstColumn,
    },
    {
      key: 'syncStatus',
      name: t('DASHBOARD_BUSINESS_DATA_UPLOADS_TABLE_SYNC_STATUS'),
      className: classes.middleColumn,
      render: ({ syncStatus }): React.ReactElement => (
        <Chip
          className={`${classes.chip} ${
            syncStatus === 'success'
              ? `${classes.tableChipActive} ${classes.chipActive}`
              : `${classes.tableChipInactive} ${classes.chipInactive}`
          }`}
          data-testid={`byod-file-sync-status-chip`}
          label={syncStatus}
          size="small"
        />
      ),
    },
    {
      key: 'timeStamp',
      className: classes.middleColumn,
      name: t('DASHBOARD_BUSINESS_DATA_UPLOADS_TABLE_TIME_STAMP'),
    },
    {
      key: 'path',
      name: t('DASHBOARD_BUSINESS_DATA_UPLOADS_TABLE_ACTION'),
      className: classes.lastColumn,
      render: ({ name, pushCommId }): React.ReactElement => {
        const handleViewFile = (event: React.MouseEvent): void => {
          const href = history.createHref({
            pathname: '/view-file',
            search: `?name=${encodeURIComponent(name)}&pcId=${encodeURIComponent(
              pushCommId,
            )}&connectionUuid=${encodeURIComponent(connection?.connectionUuid)}`,
          });
          openNewTab(event, href);
        };

        return (
          <Button
            className={classes.viewButton}
            variant="text"
            size="small"
            color="primary"
            onClick={handleViewFile}
          >
            {t('DASHBOARD_BUSINESS_DATA_UPLOADS_VIEW_FILE')}
          </Button>
        );
      },
    },
  ];

  const errorLoading = (
    <Box className={classes.errorTableContainer} data-testid="error-table">
      <ErrorIcon statusCode={500} />
      <Typography color="textSecondary" className={classes.description}>
        {t('DASHBOARD_FINANCIAL_SUMMARY_CHART_ERROR_500')}
      </Typography>
    </Box>
  );

  const noDataFound = (
    <span className={classes.noDataText}>
      {t('DASHBOARD_BUSINESS_DATA_UPLOADS_NOT_FILE_FOUND')}
    </span>
  );

  return (
    <Box>
      <p className={classes.tabBusinessTitle}>{t('DASHBOARD_BUSINESS_DATA_UPLOADS_TITLE')}</p>
      <>
        <ScrollView classNames={classes.scrollContainer}>
          <BasicTable
            className={classes.table}
            stickyHeader
            isLoading={isLoading}
            columns={columns}
            rows={rows}
            size="small"
            isPaperComponent={false}
            noData={noDataFound}
            errorLoading={error && error[0] !== 'Connection not found' ? errorLoading : null}
          />
        </ScrollView>

        {totalPages > 1 && (
          <div className={classes.pagination}>
            <Pagination
              count={totalPages}
              page={currentPage}
              defaultPage={1}
              onChange={(_, pagerNumber): void => {
                setCurrentPage(pagerNumber);
                refreshByodFileList({
                  offset: (pagerNumber - 1) * PAGINATION_LIMIT,
                });
              }}
            />
          </div>
        )}
      </>
    </Box>
  );
};

export default DataUploads;
